import { isMarketingComponent } from '@/lib/i18n-page-map';

export type InertiaEntry = 'marketing' | 'app';

export const DEFAULT_INERTIA_ENTRY: InertiaEntry = 'app';

export function resolveInertiaEntry(component: string, inertiaEntry: unknown): InertiaEntry {
    if (inertiaEntry === 'marketing' || inertiaEntry === 'app') {
        return inertiaEntry;
    }

    return isMarketingComponent(component) ? 'marketing' : 'app';
}

/**
 * Pages that only exist in the authenticated app entry (resources/js/app.tsx).
 * Guest auth (login, register, password reset, …) is also in the marketing glob
 * so home ↔ login stays an Inertia visit instead of a full document reload.
 *
 * MAINTENANCE: when adding a new authenticated-only route, add its first segment
 * here in the same PR. Covered by __tests__/app-entry.test.ts.
 */
const APP_ONLY_PATH = /\/(dashboard|settings|deposit-accounts|tenants|properties|documents|verification|create-flow|us-citizen)(\/|$|\?)/;

/**
 * Guest auth is compiled into both entries. Never force a document navigation
 * for these or the page flashes white on home → login → register → home.
 */
const SHARED_GUEST_AUTH_PATH = /\/(login|register|forgot-password|reset-password|verify-email|confirm-password|account-deleted)(\/|$|\?)/;

function pathFromHref(href: string): string {
    return href.replace(/^https?:\/\/[^/]+/, '').split('#')[0] ?? href;
}

export function isAppEntryPath(href: string): boolean {
    return APP_ONLY_PATH.test(pathFromHref(href));
}

export function isSharedGuestAuthPath(href: string): boolean {
    return SHARED_GUEST_AUTH_PATH.test(pathFromHref(href));
}

export function needsDocumentNavigation(href: string, entry: InertiaEntry): boolean {
    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return false;
    }

    if (isSharedGuestAuthPath(href)) {
        return false;
    }

    const isAppOnly = isAppEntryPath(href);

    return entry === 'marketing' ? isAppOnly : !isAppOnly;
}
